import React from 'react';

import { MarginProps } from '@opendoor/bricks/system/theme';
import { Box, Card, Flex, Text } from '@opendoor/bricks/core';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { Timeline, Step } from '@opendoor/cinderblocks/platform/Timeline';
import { InternalLink } from 'platform/react-router';
import { colors } from '@opendoor/bricks/theme/eero';

const CTA_BUTTON_MAX_WIDTH = 265;

export const analyticsPrefixTimelineCta = 'timeline-cta--';

type ITimelineCta = {
  steps: Array<Step>;
  currentStepName: Step['name'];
  title: string;
  buttonText: string;
  analyticsId: string;
  href: string;
} & MarginProps;

/**
 * @description
 * TimelineCta displays the Timeline component with title text and a CTA button.
 *
 * @param {Step[]} steps - List of steps to show in the timeline
 * @param {string} currentStepName - Name of the current step
 * @param {string} title - Title on the Timeline Card
 * @param {string} buttonText - Text of the CTA button
 * @param {string} analyticsId - Analytic ID of the CTA button
 *
 * component-test-check-ignore
 */
export const TimelineCta: React.FC<ITimelineCta> = ({
  steps,
  currentStepName,
  title,
  buttonText,
  analyticsId,
  href,
  ...marginProps
}) => {
  const { isMobile } = useDevice();
  return isMobile ? (
    <Box px={7} py="5" mt={0} width="100%" borderTop={`1px solid ${colors.neutrals30}`}>
      <InternalLink
        aria-label={null}
        analyticsName={`cake-${analyticsPrefixTimelineCta}${analyticsId}`}
        variant="primary-button"
        to={href}
        py={4}
      >
        {buttonText}
      </InternalLink>
    </Box>
  ) : (
    <Card
      role="group"
      aria-label="Next steps card"
      variant="primary"
      width="100%"
      p={[7, null, 7]} // this is necessary to overwrite the `Card` component default padding :(
      {...marginProps}
      data-testid="timeline-cta-desktop"
    >
      <Flex justifyContent="space-between" alignItems="center">
        <Text as="h2" size="s2" fontWeight="semibold" color="neutrals100">
          {title}
        </Text>
        <InternalLink
          aria-label={null}
          analyticsName={`cake-${analyticsPrefixTimelineCta}${analyticsId}`}
          variant="primary-button"
          to={href}
          width="100%"
          maxWidth={CTA_BUTTON_MAX_WIDTH}
          py={4}
        >
          {buttonText}
        </InternalLink>
      </Flex>
      <Box mt={7}>
        <Timeline ariaLabel="Selling timeline" currentStepName={currentStepName} steps={steps} />
      </Box>
    </Card>
  );
};
